import React, { useCallback, useState } from 'react';

import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Token from '@amzn/awsui-components-console/token';

import { SavedSearchEntry } from 'src/common/savedSearches';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface SavedSearchesBarProps<TState> {
  /** Current entries to display as chips. */
  readonly entries: SavedSearchEntry<TState>[];
  /** Called when the user clicks a chip's dismiss button. */
  readonly remove: (id: string) => Promise<boolean>;
  /** Applies a saved selection. */
  readonly onLoadSearch: (state: TState) => void;
  /** Bold prefix, e.g. 'Saved Searches:'. */
  readonly label?: string;
  /** Secondary hint after the chips, e.g. the maximum-kept note. */
  readonly hint?: string;
  /** `data-testid` on each chip wrapper, for counting in tests. */
  readonly itemTestId?: string;
}

/**
 * Row of saved search chips. Clicking a chip's label loads it; its trailing "×"
 * removes it immediately.
 *
 * Each chip is a Cloudscape `Token`, the design system's own chip-with-dismiss, so
 * placement, spacing, focus order and RTL come from the component.
 *
 * Each control's accessible name states its action and names the search ("Load saved
 * search Checkout", "Remove saved search Checkout"), since the group label alone and a
 * row of textless × buttons are otherwise ambiguous to a screen reader.
 */
function SavedSearchesBar<TState>({
  entries,
  remove,
  onLoadSearch,
  label,
  hint,
  itemTestId,
}: SavedSearchesBarProps<TState>) {
  const { t } = useTranslation();
  const [removeError, setRemoveError] = useState<string | null>(null);

  const handleLoadClick = useCallback(
    (searchId: string) => {
      const found = entries.find((search) => search.id === searchId);
      if (found) {
        onLoadSearch(found.state);
      }
    },
    [entries, onLoadSearch],
  );

  if (entries.length === 0) {
    return null;
  }

  return (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      {label && (
        <Box variant="span" fontWeight="bold">
          {label}
        </Box>
      )}
      {entries.map((search) => (
        <Token
          key={search.id}
          data-testid={itemTestId}
          ariaLabel={search.label}
          label={
            <Button
              variant="inline-link"
              ariaLabel={t('saved-searches-load-aria', { name: search.label })}
              onClick={() => handleLoadClick(search.id)}
            >
              {search.label}
            </Button>
          }
          dismissLabel={t('saved-searches-remove-aria', {
            name: search.label,
          })}
          onDismiss={async () => {
            setRemoveError(null);
            const success = await remove(search.id);
            if (!success) {
              // eslint-disable-next-line no-console
              console.warn('Failed to remove saved search:', search.id);
              setRemoveError(
                t('saved-searches-remove-error', { name: search.label }),
              );
            }
          }}
        />
      ))}
      {hint && (
        <Box variant="span" color="text-body-secondary" fontSize="body-s">
          {hint}
        </Box>
      )}
      {removeError && (
        <span role="alert">
          <Box variant="span" color="text-status-error" fontSize="body-s">
            {removeError}
          </Box>
        </span>
      )}
    </SpaceBetween>
  );
}

export default SavedSearchesBar;
